import { Inbox, Lock } from 'lucide-react';
import { EmptyState, ErrorBanner, Skeleton } from '@hub/ui';
import type { BladeDescriptor } from './types';

// View-primitive state catalog (HUB-276, design §5): every blade body delegates
// loading / empty / error / denied to these shared states instead of hand-rolling
// them, so primitives stay focused on their content. Built on @hub/ui
// (Skeleton / EmptyState / ErrorBanner). These are what a data-bound primitive
// (HUB-277) renders while a query is pending / empty / failed / masked.

export function BladeLoading() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-label="Loading">
      <Skeleton className="h-5 w-2/3" />
      <Skeleton className="h-5 w-full" />
      <Skeleton className="h-5 w-5/6" />
      <Skeleton className="h-5 w-1/2" />
    </div>
  );
}

export function BladeEmpty({
  title = 'Nothing here yet',
  description = 'There is nothing to show for this view.',
}: {
  title?: string;
  description?: string;
}) {
  return <EmptyState icon={Inbox} title={title} description={description} />;
}

export function BladeError({
  message = 'Something went wrong loading this view.',
}: {
  message?: string;
}) {
  return <ErrorBanner message={message} className="" />;
}

export function BladeDenied({
  description = 'You don’t have permission to view this.',
}: {
  description?: string;
}) {
  return <EmptyState icon={Lock} title="Access restricted" description={description} />;
}

export type ForcedBladeState = 'loading' | 'error' | 'denied' | 'empty';

/** A blade can be pinned to a catalog state via `descriptor.params.state` — used
 * for demos/tests today, and the seam a data-bound primitive will drive from its
 * query status (loading/error/denied) tomorrow. */
export function forcedBladeState(descriptor: BladeDescriptor): ForcedBladeState | undefined {
  const s = descriptor.params?.state;
  return s === 'loading' || s === 'error' || s === 'denied' || s === 'empty' ? s : undefined;
}
